"use client";
import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import SingleTestimonial from "./SingleTestimonial";
import { Testimonial } from "@/types/testimonial";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const RotatingTestimonials = ({ testimonials: rawTestimonials, interval }: any) => {
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const testimonials = Array.isArray(rawTestimonials) ? rawTestimonials : [];
	const count = useRef(1);
	const timer = useRef<NodeJS.Timeout | null>(null);
	const [x, setX] = useState(0);
	const [isMobile, setIsMobile] = useState(false);
	const [disableAnimation, setDisableAnimation] = useState(false)

	const clearCarouselTimer = () => {
		if (timer.current) {
			clearInterval(timer.current);
			timer.current = null;
		}
	};

	useEffect(() => {
		if (!isVideoPlaying && Array.isArray(testimonials) && testimonials.length > 1) {
			timer.current = setInterval(() => {
				try {
					if (!Array.isArray(testimonials) || testimonials.length < 2) return;
					if (count.current <= testimonials.length - 2 && !disableAnimation) {
						count.current += 1;
						setX(count.current);
					}
					else if (count.current === 1 && disableAnimation) {
						setDisableAnimation(false)
						count.current += 1
					}
					else {
						setDisableAnimation(true)
						count.current = 1;
						setX(count.current);
					}
				} catch (err) { console.error("carousel timer error:", err); }
			}, interval);

			return clearCarouselTimer;
		}
	}, [isVideoPlaying, interval, testimonials.length, disableAnimation]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 600);
		};

		// Initial check
		handleResize();

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	let pos: number[] = [];
	try {
		if (Array.isArray(testimonials) && testimonials.length > 0) {
			if (isMobile) {
				pos = testimonials.map((_: any, i: number) => i * -100);
				pos = [...pos].reverse();
			} else {
				pos = testimonials.map((_: any, i: number) => i * -100);
			}
			if (count.current >= pos.length) count.current = 0;
			if (count.current < 0) count.current = 0;
		}
	} catch (err) { console.error("pos calc error:", err); pos = []; }

	const handleVideoPlay = () => setIsVideoPlaying(true);
	const handleVideoPause = () => setIsVideoPlaying(false);

	const handleSwipe = (direction: string) => {
		try {
			if (!Array.isArray(testimonials) || testimonials.length < 2) return;
			if (direction === "right") {
				clearCarouselTimer();
				if (count.current === testimonials.length - 2) {
					setDisableAnimation(true)
					count.current = 0
				}
				else {
					setDisableAnimation(false)
					count.current += 1;
				}
			} else if (direction === "left") {
				clearCarouselTimer();
				if (count.current === 0) {
					setDisableAnimation(true)
					count.current = testimonials.length - 2
				}
				else {
					count.current -= 1;
					setDisableAnimation(false)
					setX(count.current)
				}
			}
			setX(count.current);
		} catch (err) { console.error("handleSwipe error:", err); }
	};

	const startSwipe = (startX) => {
		const onTouchEnd = (e) => {
			const endX = isMobile ? e.changedTouches[0].clientX : e.clientX;
			const deltaX = endX - startX;

			if (deltaX > 50) {
				handleSwipe("right");
			} else if (deltaX < -50) {
				handleSwipe("left");
			}

			document.removeEventListener("touchend", onTouchEnd);
			document.removeEventListener("mouseup", onTouchEnd);
		};

		document.addEventListener("touchend", onTouchEnd);
		document.addEventListener("mouseup", onTouchEnd);
	};

	if (!Array.isArray(testimonials) || testimonials.length === 0) {
		return (
			<div className="animate-pulse rounded-2xl bg-slate-200 h-64 flex items-center justify-center">
				<span className="text-slate-400 text-sm">No testimonials</span>
			</div>
		);
	}

	return (
		// <TestimonialCarousel
		//   testimonials={testimonials}
		//   handleVideoPlay={handleVideoPlay}
		//   handleVideoPause={handleVideoPause}/>
		<div className="relative overflow-hidden flex items-center">
			{/* Left Button */}
			{!isMobile && (
				<button
					onClick={() => {
						clearCarouselTimer();
						if (count.current === 0) {
							setDisableAnimation(true)
							count.current = testimonials.length - 2
						}
						else {
							count.current -= 1;
							setDisableAnimation(false)
							setX(count.current)
						}
					}}
					className="absolute left-2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md"
				>
					<ChevronLeft className="w-5 h-5 text-gray-700" />
				</button>
			)}

			{/* Carousel Track */}
			<div
				className={`flex w-full`}
				onTouchStart={(e) => startSwipe(e.touches[0].clientX)}
				onMouseDown={(e) => startSwipe(e.clientX)}
			>
			{testimonials.map((testimonial: Testimonial, index) => (
				<motion.div
					key={`${testimonial.id}-${index}`}
						className={`${isMobile ? "w-full p-2" : "w-full flex-shrink-0 p-4"}`}
						style={{ minWidth: isMobile ? "100%" : "33.3333%" }}
						animate={{
							x: `${pos[count.current] ?? 0}%`,

						}}
						transition={
							!disableAnimation
								? { type: "spring", stiffness: 60 }
								: { duration: 0 }
						}
					>
						<SingleTestimonial
							testimonial={testimonial}
							onVideoPlay={handleVideoPlay}
							onVideoPause={handleVideoPause}
						/>
					</motion.div>
				))}
			</div>

			{/* Right Button */}
			{!isMobile && (
				<button
					onClick={() => {
						clearCarouselTimer();
						if (count.current === testimonials.length - 2) {
							setDisableAnimation(true)
							count.current = 0
						}
						else {
							setDisableAnimation(false)
							count.current += 1;
							setX(count.current);
						}
					}}
					className="absolute right-2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md"
				>
					<ChevronRight className="w-5 h-5 text-gray-700" />
				</button>
			)}
		</div>
	);
};
